import { ButtonFlavor, ButtonSize, ButtonType, ExButton, ExIcon } from '@boomi/exosphere';
import { type ChangeEvent, useRef, useState } from 'react';
import AvatarEditor from 'react-avatar-editor';
import Loading from '../Loading';
import { useApp } from '../common/AppProvider';
import { translations } from '../translations/translations';
import { base64toFile } from '../utils/utils';

import './profile.css';

const Profile = () => {
    const {
        userInfo,
        language,
        syncAndUpdate,
        currentUserProfileImage,
        setCurrentUserProfileImage,
        isLoading,
    } = useApp();
    const avatarEditorRef = useRef(null);
    const [scale, setScale] = useState<number>(1);
    const [loadedImage, setLoadedImage] = useState<string | null>(null);
    const [imageChanged, setImageChanged] = useState<boolean>(false);

    const handleLoadImage = (event: ChangeEvent) => {
        const imageFiles = (event.target as HTMLInputElement).files;
        if (imageFiles?.length) {
            setLoadedImage(URL.createObjectURL(imageFiles[0]));
        }
    };

    const handleScaleImage = (event: ChangeEvent) => {
        setScale(Number.parseFloat((event.target as HTMLInputElement).value));
    };

    const handleSaveImage = () => {
        if (avatarEditorRef.current) {
            // Base64 encoded image (JPEG with image quality set to 75%)
            // TS complains that getImageScaledToCanvas does not exist on the canvas element,
            // but if getImageScaledToCanvas is removed this no longer works ಠ_ಠ
            const profileImageBase64 = (avatarEditorRef.current as HTMLCanvasElement)
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                .getImageScaledToCanvas()
                // ^ scale the image down to the canvas size, because we don't want or need
                // to use a full resolution image as those could be huge
                .toDataURL('image/jpeg', 0.85)
                .split(',')[1];

            syncAndUpdate([], profileImageBase64, false);
            setImageChanged(false);
        }
    };

    const handleDeleteImage = () => {
        if (avatarEditorRef.current) {
            syncAndUpdate([], '', false);
            setLoadedImage(null);
            setImageChanged(false);
            setCurrentUserProfileImage(null);
        }
    };

    return (
        <>
            <div className="profile-page">
                <h3 className="title xxl">
                    {translations[language].USER_PROFILE_greeting}
                    {userInfo && (
                        <span className="welcome-name">
                            , {(userInfo?.['given_name'] as string) ?? ''}
                        </span>
                    )}
                    !
                </h3>

                {/* Avatar */}
                <section className="avatar-section ta-tile">
                    <div className="profile-picture-editor">
                        <div className="profile-picture">
                            <figure className="profile-picture-placeholder">
                                <ExIcon icon="User" hide-browser-tooltip className="icon-xl" />
                            </figure>
                            <AvatarEditor
                                className="profile-picture-canvas"
                                ref={avatarEditorRef}
                                image={
                                    loadedImage
                                        ? loadedImage
                                        : currentUserProfileImage
                                          ? base64toFile(
                                                currentUserProfileImage,
                                                'current_image.jpg',
                                            )
                                          : ''
                                }
                                width={300}
                                height={300}
                                border={0}
                                borderRadius={150}
                                color={[255, 255, 255, 0.75]} // RGBA
                                scale={scale}
                                rotate={0}
                                onImageChange={() => {
                                    !imageChanged && setImageChanged(true);
                                }}
                            />
                        </div>

                        {/* Allow image scaling only for newly loaded images. */}
                        {loadedImage && (
                            <div className="profile-picture-scale">
                                <span className="profile-picture-scale-label">
                                    <ExIcon icon="User" hide-browser-tooltip className="icon-sm" />
                                    {translations[language].USER_PROFILE_scale_image}
                                    <ExIcon icon="User" hide-browser-tooltip className="icon-lg" />
                                </span>
                                <input
                                    className="profile-picture-scale-input"
                                    type="range"
                                    min="1"
                                    max="2"
                                    step="0.01"
                                    defaultValue="1"
                                    onChange={handleScaleImage}
                                />
                            </div>
                        )}

                        <div className="profile-picture-buttons">
                            <div className="profile-picture-load">
                                {/*
                                    We can't properly style the file input,
                                    so it will be hidden and triggered via a custom button
                                */}
                                <input
                                    className="profile-picture-load-input"
                                    type="file"
                                    id="profilePictureLoadInput"
                                    accept="image/png, image/jpeg"
                                    onChange={handleLoadImage}
                                    // Clear the file input so that we can (re)select the same image again.
                                    onClick={(evt) => {
                                        (evt.target as HTMLInputElement).value = '';
                                    }}
                                />
                                <ExButton
                                    className="exo-button"
                                    flavor={ButtonFlavor.BASE}
                                    size={ButtonSize.LARGE}
                                    type={ButtonType.SECONDARY}
                                    onClick={() =>
                                        document.getElementById('profilePictureLoadInput')!.click()
                                    }
                                >
                                    {translations[language].USER_PROFILE_change_image}
                                </ExButton>
                            </div>

                            <ExButton
                                className="exo-button"
                                flavor={ButtonFlavor.BASE}
                                size={ButtonSize.LARGE}
                                type={ButtonType.PRIMARY}
                                onClick={handleSaveImage}
                                // Enable Save button if an image was just added or changed using the Change button.
                                disabled={!(imageChanged && loadedImage)}
                            >
                                {translations[language].USER_PROFILE_save_image}
                            </ExButton>

                            <ExButton
                                className="exo-button"
                                flavor={ButtonFlavor.RISKY}
                                size={ButtonSize.LARGE}
                                type={ButtonType.PRIMARY}
                                onClick={handleDeleteImage}
                                // Enable Delete button only if an image was saved/loaded from the DB.
                                disabled={!currentUserProfileImage}
                            >
                                {translations[language].COMMON_delete}
                            </ExButton>
                        </div>
                    </div>
                </section>
            </div>
            {isLoading ? <Loading message={translations[language].COMMON_loading} /> : null}
        </>
    );
};

export default Profile;
